import { useState, useEffect } from "react";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../firebase";
import StoryModal from "../components/StoryModal";
import TwitterEmbed from "../components/TwitterEmbed";
import "../styles/global.css";
import ThemeToggle from "../components/ThemeToggle";
import { motion } from "framer-motion";
import StoryTimeline from "../components/StoryTimeline";
import Header from "../components/Header";
import ModernCard from "../components/ui/ModernCard"; // Import the new card component
import BackgroundEffect from "../components/BackgroundEffect";
import FancyModal from "../components/ui/FancyModal";

interface Story {
  id?: string;
  text: string;
  xLink?: string;
  status: "pending" | "approved";
  createdAt: any;
}

function Home() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [approvedStories, setApprovedStories] = useState<Story[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const [selectedStory, setSelectedStory] = useState<Story | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  const openStoryDetail = (story: Story) => {
    setSelectedStory(story);
    setIsDetailModalOpen(true);
  };

  useEffect(() => {
    fetchApprovedStories();
  }, []);

  const formatDate = (timestamp: any): string => {
    if (!timestamp) return "";
    try {
      const date = timestamp.toDate();
      return date.toLocaleDateString();
    } catch (e) {
      return "";
    }
  };

  const fetchApprovedStories = async () => {
    try {
      setLoading(true);
      const q = query(
        collection(db, "stories"),
        where("status", "==", "approved")
      );
      const querySnapshot = await getDocs(q);
      const stories = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as Story[];

      stories.sort(
        (a, b) =>
          b.createdAt?.toDate()?.getTime() - a.createdAt?.toDate()?.getTime()
      );
      setApprovedStories(stories);
      setError(null);
    } catch (err) {
      console.error("Error fetching stories:", err);
      setError("Failed to load stories. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const categorizeStory = (text: string): string => {
    const keywords: Record<string, string> = {
      breast: "Breast Cancer",
      lung: "Lung Cancer",
      colon: "Colon Cancer",
      leukemia: "Leukemia",
      lymphoma: "Lymphoma",
      melanoma: "Melanoma",
      "skin cancer": "Skin Cancer",
      prostate: "Prostate Cancer",
      ovarian: "Ovarian Cancer",
      pancreatic: "Pancreatic Cancer",
      thyroid: "Thyroid Cancer",
      diet: "Dietary Approach",
      nutrition: "Nutrition",
      meditation: "Meditation",
      holistic: "Holistic",
      alternative: "Alternative",
      herbal: "Herbal Remedy",
    };

    const lowerText = text.toLowerCase();
    for (const [keyword, category] of Object.entries(keywords)) {
      if (lowerText.includes(keyword)) {
        return category;
      }
    }

    return "Personal Journey";
  };

  // Generate a title based on content
  const generateTitle = (text: string, xLink?: string): string => {
    if (xLink) {
      return "Shared Experience";
    }

    // Look for patterns like "I beat X" or "My journey with X"
    const textLower = text.toLowerCase();
    if (textLower.includes("beat") || textLower.includes("overcame")) {
      return "Survivor Story";
    }

    if (textLower.includes("journey") || textLower.includes("path")) {
      return "Healing Journey";
    }

    if (
      textLower.includes("diet") ||
      textLower.includes("nutrition") ||
      textLower.includes("food")
    ) {
      return "Dietary Approach";
    }

    if (textLower.includes("treatment") || textLower.includes("therapy")) {
      return "Treatment Experience";
    }

    return "Personal Story";
  };

  // Extract a preview from longer text
  const extractPreview = (text: string, maxLength: number = 150): string => {
    if (text.length <= maxLength) return text;

    // Try to find a sentence end for a clean break
    const sentenceEndIndex = text.substr(0, maxLength).lastIndexOf(".");
    if (sentenceEndIndex > maxLength * 0.7) {
      return text.substr(0, sentenceEndIndex + 1);
    }

    // Otherwise, break at a word boundary
    return text.substr(0, maxLength).trim() + "...";
  };

  // Check if a link is an X/Twitter link
  const isXLink = (url: string) => {
    return url && (url.includes("twitter.com") || url.includes("x.com"));
  };

  // Function to generate a consistent color based on story content
  const getStoryColor = (text: string): string => {
    // Cancer awareness ribbon colors
    const cancerColors = [
      "#e5097f", // Pink (Breast cancer)
      "#00a2e0", // Blue (Prostate cancer)
      "#ffc80c", // Yellow (Bone cancer)
      "#7c2d98", // Purple (Pancreatic cancer)
      "#ed1c24", // Red (AIDS-related cancers)
      "#006633", // Green (Liver cancer)
      "#fc5d1f", // Orange (Leukemia)
      "#682c91", // Lavender (All cancers)
    ];

    // Simple hash function to get a consistent color based on content
    let hash = 0;
    for (let i = 0; i < text.length; i++) {
      hash = text.charCodeAt(i) + ((hash << 5) - hash);
    }

    return cancerColors[Math.abs(hash) % cancerColors.length];
  };

  return (
    <div className={`app ${isMobile ? "mobile" : ""}`}>
      <BackgroundEffect />
      <Header setIsModalOpen={setIsModalOpen} />

      <main className="container">
        <section className="stories-section">
          <h2>Stories of Hope</h2>
          <p className="subtitle">
            Read inspiring stories from others who have taken alternative
            approaches
          </p>

          {loading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Loading stories of hope...</p>
            </div>
          ) : error ? (
            <div className="error-message card">
              <p>{error}</p>
              <button
                onClick={fetchApprovedStories}
                className="btn btn-secondary"
              >
                Try Again
              </button>
            </div>
          ) : approvedStories.length === 0 ? (
            <div className="card empty-state">
              <p>No stories have been shared yet. Be the first!</p>
              <button
                className="btn btn-primary"
                onClick={() => setIsModalOpen(true)}
              >
                Share Your Story
              </button>
            </div>
          ) : (
            <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="stories-container"
          >
            {!isMobile ? (
              <div className="stories-grid">
                {approvedStories.map((story) => {
                  // Get color based on content
                  const color = getStoryColor(story.text);
                  
                  // Format text for preview (keep it short)
                  const previewText = story.text.length > 120 
                    ? `${story.text.substring(0, 120)}...` 
                    : story.text;
                  
                  // Generate a title based on content
                  const storyTitle = generateTitle(story.text, story.xLink);
                  
                  // Try to determine a category
                  const category = categorizeStory(story.text);
                  
                  // Format date
                  const dateStr = story.createdAt ? formatDate(story.createdAt) : '';
                  
                  return (
                    <ModernCard
                      key={story.id}
                      title={storyTitle}
                      content={previewText}
                      color={color}
                      link={story.xLink || null}
                      date={dateStr}
                      category={category}
                      onClick={() => {
                        if (story.xLink) {
                          window.open(story.xLink, "_blank");
                        } else {
                          // Open detail modal directly
                          openStoryDetail(story);
                        }
                      }}
                    />
                  );
                })}
              </div>
            ) : (
              <div className="stories-list">
                <StoryTimeline stories={approvedStories} />
              </div>
            )}
          </motion.div>
        )}
      </section>
        <section className="analytics-promo">
          <div className="container">
            <h2>Cancer Treatment Analytics</h2>
            <p>
              Explore trends and patterns in alternative cancer treatments
              through our community data.
            </p>
            <a href="/analytics" className="btn btn-secondary">
              View Analytics Dashboard
            </a>
          </div>
        </section>
        <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8, duration: 0.5 }}
      >
        <ThemeToggle />
      </motion.div>
      </main>

      <footer className="site-footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-info">
              <p>© {new Date().getFullYear()} My Abnormal Story</p>
              <p className="footer-tagline">
                Sharing alternative cancer treatment journeys to keep the fight
                alive.
              </p>
            </div>
            <div className="footer-links">
              <a href="/" className="admin-link">
                Home
              </a>
              <a href="/about" className="admin-link">
                About Us
              </a>
              <a href="/privacy" className="admin-link">
                Privacy
              </a>
              <a href="/resources" className="admin-link">
                Resources
              </a>
              <a href="/analytics" className="admin-link">
                Analytics
              </a>
              <a href="/admin-login" className="admin-link">
                Admin
              </a>
            </div>
          </div>
        </div>
      </footer>

      <StoryModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
}

export default Home;

const useMediaQuery = (query: { maxWidth: number }) => {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    const media = window.matchMedia(`(max-width: ${query.maxWidth}px)`);
    setMatches(media.matches);

    const listener = (e: MediaQueryListEvent) => setMatches(e.matches);
    media.addEventListener("change", listener);

    return () => media.removeEventListener("change", listener);
  }, [query.maxWidth]);

  return matches;
};

